import React, { useEffect, useState, useRef } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Megaphone, Play, Volume2, Trophy, Ticket, Plus, Link as LinkIcon, Copy, Wallet, Upload, CheckCircle2, XCircle, Users, Trash2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage } from '@/lib/upload';
import { UploadField } from '@/components/UploadField';
import { ImageZoom } from '@/components/ImageZoom';

const TicketGrid = ({ ticket, called }) => (
  <div className="grid grid-cols-9 gap-0.5 bg-[var(--bz-line)] p-1 rounded-sm" data-testid={`ticket-${ticket.ticket_id}`}>
    {ticket.grid.map((row, r) => row.map((n, c) => (
      <div key={`${r}-${c}`}
        className={`aspect-square text-[11px] font-bold rounded-sm grid place-items-center ${!n ? 'bg-white/40' : called.includes(n) ? 'bg-[#1D4ED8] text-white' : 'bg-white text-[var(--bz-ink)]'}`}>
        {n || ''}
      </div>
    )))}
  </div>
);

const FunTime = () => {
  const { user } = useAuth();
  const [myRooms, setMyRooms] = useState({ as_team_leader: [], as_player: [] });
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [joinTokenInput, setJoinTokenInput] = useState('');
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditForm, setCreditForm] = useState({ amount: 0, utr: '', screenshot_url: '' });
  const [creditRequests, setCreditRequests] = useState([]);
  const [creditUploading, setCreditUploading] = useState(false);
  const [tlSelfOpen, setTlSelfOpen] = useState(false);
  const [tlSelfAmount, setTlSelfAmount] = useState(0);
  const [tlSelfBusy, setTlSelfBusy] = useState(false);
  const [playersData, setPlayersData] = useState({ players: [], totals: { tickets: 0, revenue: 0, tl_fee_earned: 0, prize_pool: 0 } });
  const [twdRequests, setTwdRequests] = useState([]);
  const [shareOpen, setShareOpen] = useState(false);
  const [inviteTo, setInviteTo] = useState({ name: '', tickets: 1 });
  const [form, setForm] = useState({
    name: 'INDIVALIFE Tambola', ticket_price: 50, max_players: 100,
    is_multi_ticket: true, tl_fee_pct: 2,
    custom_prizes: { jaldi5: 100, line1: 100, line2: 100, line3: 100, full_house: 500 },
    tl_payment: { upi: '', phone: user?.phone || '', qr_image_url: '', bank: '', account: '', ifsc: '', instructions: 'Pay the ticket price × number of tickets to my UPI, then upload screenshot + UTR for credit approval.' },
  });

  const loadMy = async () => { const { data } = await api.get('/tambola/my-rooms'); setMyRooms(data); };
  useEffect(() => { loadMy(); }, []);

  // Poll active room
  useEffect(() => {
    if (!active) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get(`/tambola/rooms/${active.room_id}`);
        setActive(data);
        if (active.team_leader_id === user.user_id) {
          const cr = await api.get(`/tambola/rooms/${active.room_id}/credit-requests`);
          setCreditRequests(cr.data);
          const pl = await api.get(`/tambola/rooms/${active.room_id}/players`);
          setPlayersData(pl.data);
          try { const tw = await api.get(`/tambola/rooms/${active.room_id}/withdraw-requests`); setTwdRequests(tw.data); } catch (e) { console.warn('tambola withdraw poll', e); }
        }
      } catch (e) { console.warn('tambola poll', e); }
    }, 3000);
    return () => clearInterval(interval);
  }, [active, user]);

  const createRoom = async () => {
    if (!user?.is_member && !user?.is_admin) { toast.error('Website Purchase required to host'); return; }
    if (!form.tl_payment.upi && !form.tl_payment.phone && !form.tl_payment.qr_image_url) {
      toast.error('Add at least one payment method (UPI / phone / QR)');
      return;
    }
    try {
      const { data } = await api.post('/tambola/rooms', form);
      toast.success('Room created'); setCreating(false); loadMy(); setActive(data); setShareOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openRoom = async (room) => {
    try {
      const { data } = await api.get(`/tambola/rooms/${room.room_id}`);
      setActive(data);
      const t = await api.get(`/tambola/rooms/${room.room_id}/my_tickets`);
      setTickets(t.data);
      if (data.team_leader_id === user.user_id) {
        const cr = await api.get(`/tambola/rooms/${data.room_id}/credit-requests`);
        setCreditRequests(cr.data);
        const pl = await api.get(`/tambola/rooms/${data.room_id}/players`);
        setPlayersData(pl.data);
      }
    } catch { toast.error('Cannot open room'); }
  };

  const joinViaLink = async () => {
    const token = joinTokenInput.trim().split('/').pop().split('?').pop().replace('#', '');
    if (!token) { toast.error('Paste a valid invite link or token'); return; }
    try {
      const { data } = await api.get(`/tambola/rooms/by-token/${token}`);
      setActive(data); setJoinTokenInput('');
      setCreditForm(f => ({ ...f, amount: data.ticket_price || 50 }));
      toast.success(`Joined ${data.name} — pay the team leader to get credits.`);
    } catch (e) { toast.error('Invalid join link'); }
  };

  const buyTicket = async (qty = 1) => {
    try {
      const { data } = await api.post(`/tambola/rooms/${active.room_id}/buy`, { room_id: active.room_id, quantity: qty });
      setTickets(t => [...t, ...data.tickets]);
      toast.success(`Bought ${data.tickets.length} ticket(s) · ₹${data.spent} · balance ₹${data.balance}`);
      const { data: refreshed } = await api.get(`/tambola/rooms/${active.room_id}`);
      setActive(refreshed);
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail.startsWith('insufficient_credits')) {
        toast.error('Insufficient credits — request credit from team leader first');
        setCreditOpen(true);
      } else toast.error(detail || 'Could not buy ticket');
    }
  };

  const startRoom = async () => {
    primeSpeech();
    await api.post(`/tambola/rooms/${active.room_id}/start`); toast.success('Game started'); loadMy();
  };

  const wsRef = useRef(null);
  const spokenRef = useRef(new Set());
  const primedRef = useRef(false);
  const [soundOn, setSoundOn] = useState(true);
  const [voiceReady, setVoiceReady] = useState(false);

  // Pre-load voice list (Chrome loads voices asynchronously)
  useEffect(() => {
    if (!('speechSynthesis' in window)) return;
    const load = () => {
      const v = window.speechSynthesis.getVoices();
      if (v && v.length) setVoiceReady(true);
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { try { window.speechSynthesis.onvoiceschanged = null; } catch (_e) { /* ignore */ } };
  }, []);

  // Prime speech-synthesis on first user gesture (mobile browsers block autoplay TTS).
  // Call this from any user-initiated click (start/call/claim/join) — once primed, every
  // utterance plays without user interaction for the rest of the session.
  const primeSpeech = () => {
    if (primedRef.current) return;
    try {
      if (!('speechSynthesis' in window)) return;
      const u = new window.SpeechSynthesisUtterance(' ');
      u.volume = 0; // silent unlock
      window.speechSynthesis.speak(u);
      primedRef.current = true;
    } catch (_e) { /* ignore */ }
  };

  const _pickVoice = () => {
    try {
      const voices = window.speechSynthesis.getVoices() || [];
      // Prefer English India, then any English
      return voices.find(v => /en[-_]IN/i.test(v.lang))
        || voices.find(v => /^en/i.test(v.lang))
        || voices[0]
        || null;
    } catch (_e) { return null; }
  };

  const _playChime = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = 'sine'; o.frequency.value = 880;
      g.gain.setValueAtTime(0.0001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.25);
      o.start();
      o.stop(ctx.currentTime + 0.28);
    } catch (_e) { /* ignore */ }
  };

  // Helper: speak with friendly Tambola call format
  const speakNumber = (n) => {
    _playChime();
    if (!soundOn) return;
    try {
      if (!('speechSynthesis' in window)) return;
      // Some browsers leave the queue paused after long idle — resume to be safe
      try { window.speechSynthesis.resume(); } catch (_e) { /* ignore */ }
      window.speechSynthesis.cancel(); // flush any stuck queue
      const decade = Math.floor((n - 1) / 10) * 10 + 1;
      const decadeEnd = Math.min(decade + 9, 90);
      const utter = new window.SpeechSynthesisUtterance(`Number ${n}. Between ${decade} and ${decadeEnd}. ${n}.`);
      const voice = _pickVoice(); if (voice) utter.voice = voice;
      utter.rate = 0.85;
      utter.pitch = 1.1;
      utter.volume = 1;
      utter.lang = 'en-IN';
      window.speechSynthesis.speak(utter);
    } catch (_e) { /* ignore */ }
  };

  const speakWinner = (prizeLabel, winnerName) => {
    _playChime();
    if (!soundOn) return;
    try {
      if (!('speechSynthesis' in window)) return;
      try { window.speechSynthesis.resume(); } catch (_e) { /* ignore */ }
      window.speechSynthesis.cancel();
      const utter = new window.SpeechSynthesisUtterance(`${prizeLabel}! Congratulations ${winnerName}.`);
      const voice = _pickVoice(); if (voice) utter.voice = voice;
      utter.rate = 0.9;
      utter.pitch = 1.2;
      utter.volume = 1;
      utter.lang = 'en-IN';
      window.speechSynthesis.speak(utter);
    } catch (_e) { /* ignore */ }
  };

  // WebSocket subscription — broadcasts every called number + winner to ALL room participants
  useEffect(() => {
    if (!active) return;
    const base = process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws');
    const cookie = document.cookie.split('; ').find(c => c.startsWith('session_token='));
    const tok = cookie ? cookie.split('=')[1] : '';
    if (!tok) return;
    try {
      const ws = new WebSocket(`${base}/ws?token=${tok}`);
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.event === 'tambola_number_called' && data.room_id === active.room_id) {
            // De-dupe (in case backend retries / multiple ws clients)
            const key = `${data.room_id}:${data.number}`;
            if (!spokenRef.current.has(key)) {
              spokenRef.current.add(key);
              speakNumber(data.number);
            }
            setActive(a => a && a.room_id === data.room_id ? { ...a, called_numbers: data.called_numbers } : a);
          } else if (data.event === 'tambola_winner' && data.room_id === active.room_id) {
            const key = `winner:${data.room_id}:${data.prize}:${data.winner_name}`;
            if (!spokenRef.current.has(key)) {
              spokenRef.current.add(key);
              speakWinner(data.prize_label, data.winner_name);
              toast.success(`🏆 ${data.prize_label} — ${data.winner_name}`);
            }
            // refresh winners list
            api.get(`/tambola/rooms/${active.room_id}`).then(r => setActive(r.data)).catch(() => {});
          } else if (data.event === 'tambola_room_deleted' && data.room_id === active.room_id) {
            toast('Room was deleted by the team leader');
            setActive(null); setTickets([]); loadMy();
          }
        } catch (_e) { /* ignore */ }
      };
      wsRef.current = ws;
      return () => { try { ws.close(); } catch (_e) { /* ignore */ } };
    } catch (_e) { /* ignore */ }
  }, [active?.room_id]);

  const callNumber = async () => {
    primeSpeech();
    try {
      const { data } = await api.post(`/tambola/rooms/${active.room_id}/call`);
      if (data.number) {
        // Local optimistic update (backend WS also broadcasts to all)
        setActive(a => ({ ...a, called_numbers: data.called_numbers }));
      } else if (data.done) {
        toast('All 90 numbers called');
      }
    } catch { toast.error('Cannot call'); }
  };

  const claim = async (ticketId, prize) => {
    primeSpeech();
    const fd = new FormData(); fd.append('prize', prize); fd.append('ticket_id', ticketId);
    try {
      const { data } = await api.post(`/tambola/rooms/${active.room_id}/claim`, fd);
      // Local toast — the audio announcement to all comes via WS broadcast
      toast.success(`🏆 Claim accepted: ${prize.replace('_', ' ')}`);
      setActive(a => ({ ...a, winners: data.winners }));
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid claim'); }
  };

  const uploadCreditScreenshot = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const res = await uploadImage(file);
    if (res) setCreditForm(f => ({ ...f, screenshot_url: res.url }));
    e.target.value = '';
  };

  const submitCreditRequest = async () => {
    if (!creditForm.amount || !creditForm.utr || !creditForm.screenshot_url) {
      toast.error('Amount, UTR and screenshot all required'); return;
    }
    try {
      await api.post(`/tambola/rooms/${active.room_id}/credit-request`, creditForm);
      toast.success('Credit request sent to team leader — wait for approval');
      setCreditOpen(false); setCreditForm({ amount: active.ticket_price, utr: '', screenshot_url: '' });
    } catch { toast.error('Failed'); }
  };

  const submitTlSelfCredit = async () => {
    const amt = Number(tlSelfAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setTlSelfBusy(true);
    try {
      const { data } = await api.post(`/tambola/rooms/${active.room_id}/tl-self-credit`, { amount: amt });
      toast.success(`Self-deposit ₹${amt} added · balance ₹${data.balance}`);
      setTlSelfOpen(false); setTlSelfAmount(0);
      const { data: refreshed } = await api.get(`/tambola/rooms/${active.room_id}`);
      setActive(refreshed);
      const pl = await api.get(`/tambola/rooms/${active.room_id}/players`);
      setPlayersData(pl.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Self-deposit failed');
    } finally {
      setTlSelfBusy(false);
    }
  };

  const undoTicket = async (ticketId) => {
    if (!window.confirm('Refund this ticket? Credits will be returned to your balance.')) return;
    try {
      const { data } = await api.delete(`/tambola/tickets/${ticketId}`);
      setTickets(t => t.filter(x => x.ticket_id !== ticketId));
      toast.success(`Refunded ₹${data.refunded} · balance ₹${data.balance}`);
      const { data: refreshed } = await api.get(`/tambola/rooms/${active.room_id}`);
      setActive(refreshed);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Undo failed');
    }
  };

  const deleteRoom = async () => {
    if (!window.confirm(`Delete "${active.name}"? This permanently removes the room, tickets and credits. Cannot be undone.`)) return;
    try {
      await api.delete(`/tambola/rooms/${active.room_id}`);
      toast.success('Room deleted');
      setActive(null);
      setTickets([]);
      loadMy();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete');
    }
  };

  const decideCredit = async (creditId, approve) => {
    try {
      await api.post('/tambola/credit-decide', { credit_id: creditId, approve });
      toast.success(approve ? 'Credit approved' : 'Credit rejected');
      const cr = await api.get(`/tambola/rooms/${active.room_id}/credit-requests`);
      setCreditRequests(cr.data);
    } catch { toast.error('Failed'); }
  };

  const inviteLink = (room) => `${window.location.origin}/fun-time?join=${room.join_token}`;
  const copyLink = (room) => {
    navigator.clipboard.writeText(inviteLink(room));
    toast.success('Invite link copied');
  };

  const inviteMessage = (room, name = '', tickets = 1) => {
    const amount = tickets * (room.ticket_price || 0);
    const pay = room.tl_payment || {};
    const payTo = pay.upi ? `UPI ${pay.upi}` : pay.phone ? `PhonePe/GPay ${pay.phone}` : 'the team leader';
    return `Hi${name ? ' ' + name : ''}, please pay ₹${amount} to ${payTo} for ${tickets} ticket${tickets > 1 ? 's' : ''} in tonight's INDIVALIFE Tambola "${room.name}".

After payment, open the link below, tap "Add credit", upload your screenshot + UTR, and I'll approve so you can play:
${inviteLink(room)}

— ${user.name}`;
  };

  const copyInviteMessage = (room) => {
    navigator.clipboard.writeText(inviteMessage(room, inviteTo.name, inviteTo.tickets));
    toast.success('WhatsApp message copied');
  };

  // Auto-pick up ?join=token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('join');
    if (token) {
      setJoinTokenInput(token);
      (async () => {
        try {
          const { data } = await api.get(`/tambola/rooms/by-token/${token}`);
          setActive(data); setCreditForm(f => ({ ...f, amount: data.ticket_price }));
          toast.success(`Joined ${data.name}`);
          window.history.replaceState(null, '', '/fun-time');
        } catch { toast.error('Invalid invite link'); }
      })();
    }
  }, []);

  if (active) {
    const isTL = active.team_leader_id === user.user_id;
    const balance = active.my_balance ?? 0;
    return (
      <div className="p-4 sm:p-6 max-w-screen-xl mx-auto">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <Button variant="outline" size="sm" onClick={() => { setActive(null); setTickets([]); setCreditRequests([]); }} data-testid="back-to-lobby">← Back</Button>
            <h1 className="text-2xl font-black tracking-tighter display mt-2">{active.name}</h1>
            <div className="text-xs text-[var(--bz-muted)] mt-1">Team leader: <b>{active.team_leader_name}</b> · {active.is_multi_ticket ? 'Multi-ticket' : 'Single-ticket'} · ₹{active.ticket_price}/ticket · TL fee {active.tl_fee_pct}%</div>
          </div>
          <div className="flex gap-2 items-center">
            <div className="bz-card px-3 py-2 flex items-center gap-2" data-testid="my-balance">
              <Wallet className="h-4 w-4 text-[#1E3A8A]" />
              <div className="text-xs text-[var(--bz-muted)]">My credits</div>
              <div className="text-lg font-black font-mono-bz">₹{balance}</div>
            </div>
            {!isTL && balance > 0 && (
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    await api.post(`/tambola/rooms/${active.room_id}/withdraw-request`);
                    toast.success('Withdrawal request sent to team leader');
                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                }}
                className="gap-2"
                data-testid="request-tambola-withdraw"
              >
                <Wallet className="h-4 w-4" />Withdraw ₹{balance}
              </Button>
            )}
            {!isTL && (
              <Button onClick={() => { setCreditForm(f => ({ ...f, amount: active.ticket_price })); setCreditOpen(true); }} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2" data-testid="request-credit-btn">
                <Upload className="h-4 w-4" />Add credit
              </Button>
            )}
            {isTL && (
              <>
                <Button variant="outline" onClick={() => { setTlSelfAmount(active.ticket_price); setTlSelfOpen(true); }} className="gap-2 border-[#1D4ED8]/40 text-[#1E3A8A] hover:bg-[#EAF1FF]" data-testid="tl-self-deposit-btn"><Wallet className="h-4 w-4" />Self deposit</Button>
                <Button variant="outline" onClick={() => setShareOpen(true)} className="gap-2" data-testid="copy-invite-btn"><LinkIcon className="h-4 w-4" />Invite players</Button>
                <Button variant="outline" onClick={deleteRoom} className="gap-2 border-red-500/40 text-red-600 hover:bg-red-50" data-testid="delete-room-btn"><Trash2 className="h-4 w-4" />Delete room</Button>
              </>
            )}
            <Button variant="outline" onClick={() => { primeSpeech(); setSoundOn(s => !s); if (soundOn) { try { window.speechSynthesis?.cancel(); } catch (_e) { /* ignore */ } } else { speakNumber(Math.floor(Math.random() * 90) + 1); } }} className={`gap-2 ${soundOn ? 'border-[#1D4ED8]/40 text-[#1E3A8A]' : 'border-red-300 text-red-600'}`} data-testid="sound-toggle-btn" title={voiceReady ? '' : 'Voices loading…'}>
              <Volume2 className="h-4 w-4" />{soundOn ? 'Sound ON' : 'Sound OFF'}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold display flex items-center gap-2"><Volume2 className="h-4 w-4 text-[#1E3A8A]" /> Called numbers</div>
              <Badge>{active.called_numbers?.length || 0}/90</Badge>
            </div>
            {active.called_numbers?.length > 0 && (
              <div className="mb-4 flex items-center justify-center">
                <div key={active.called_numbers[active.called_numbers.length - 1]} className="h-28 w-28 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] text-white text-5xl font-black display grid place-items-center shadow-2xl ring-4 ring-[#1D4ED8]/30 animate-in zoom-in-50 duration-500" data-testid="last-called-number">
                  {active.called_numbers[active.called_numbers.length - 1]}
                </div>
              </div>
            )}
            <div className="grid grid-cols-10 gap-1">
              {Array.from({ length: 90 }, (_, i) => i + 1).map(n => (
                <div key={n} className={`aspect-square grid place-items-center text-xs font-bold rounded-sm transition-all ${active.called_numbers?.includes(n) ? 'bg-[#1D4ED8] text-white scale-100' : 'bg-[var(--bz-soft)] text-[var(--bz-muted)]'}`}>{n}</div>
              ))}
            </div>
          </Card>

          <div className="space-y-4">
            {/* TL Payment details (visible to players) */}
            {!isTL && active.tl_payment && (Object.values(active.tl_payment).some(Boolean)) && (
              <Card className="p-4 border-[#1D4ED8]/40">
                <div className="font-bold display mb-2 text-sm">Pay team leader</div>
                {active.tl_payment.upi && <div className="text-xs mb-1"><b>UPI:</b> <span className="font-mono-bz">{active.tl_payment.upi}</span></div>}
                {active.tl_payment.phone && <div className="text-xs mb-1"><b>Phone:</b> {active.tl_payment.phone}</div>}
                {active.tl_payment.bank && <div className="text-xs mb-1"><b>Bank:</b> {active.tl_payment.bank} · A/C {active.tl_payment.account} · IFSC {active.tl_payment.ifsc}</div>}
                {active.tl_payment.qr_image_url && <img src={active.tl_payment.qr_image_url} alt="QR" className="h-32 mt-2 rounded-sm" />}
                {active.tl_payment.instructions && <p className="text-[11px] text-[var(--bz-muted)] mt-2">{active.tl_payment.instructions}</p>}
              </Card>
            )}

            <Card className="p-4">
              <div className="font-bold display mb-2 flex items-center gap-2"><Trophy className="h-4 w-4 text-[#1E3A8A]" />Prizes</div>
              {(active.is_multi_ticket ? active.prizes : active.custom_prizes) && Object.entries((active.is_multi_ticket ? active.prizes : active.custom_prizes) || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm py-1">
                  <span className="capitalize">{k.replace('_', ' ')}</span>
                  <span className="font-mono-bz font-bold">₹{Math.round(v)}</span>
                </div>
              ))}
              <div className="border-t mt-2 pt-2 space-y-1">
                {active.winners && Object.entries(active.winners).filter(([_, v]) => v).map(([k, v]) => {
                  const wlist = Array.isArray(v?.winners) ? v.winners : (v?.user_id ? [v] : []);
                  const prizeAmt = ((active.is_multi_ticket ? active.prizes : active.custom_prizes) || {})[k] || 0;
                  const perWinner = wlist.length > 0 ? Math.round(prizeAmt / wlist.length) : 0;
                  return (
                    <div key={k} className="text-xs">
                      <div className="flex justify-between"><span className="capitalize font-semibold">{k.replace('_', ' ')}</span><span className="text-[#1E3A8A] font-mono-bz">₹{perWinner} × {wlist.length}</span></div>
                      <div className="flex flex-wrap gap-1 mt-0.5">
                        {wlist.map((w, i) => <span key={w.user_id || `${k}-${i}`} className="bg-[var(--bz-green-tint)] text-[#1E3A8A] rounded-full px-2 py-0.5 text-[10px]">{w.user_name}</span>)}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {isTL && (
              <Card className="p-4">
                <div className="font-bold display mb-2 text-sm flex items-center gap-2"><Users className="h-4 w-4 text-[#1E3A8A]" />Credit requests ({creditRequests.filter(c => c.status === 'pending').length} pending)</div>
                <div className="space-y-2 max-h-[260px] overflow-y-auto bz-scroll">
                  {creditRequests.length === 0 && <div className="text-xs text-[var(--bz-muted)]">No requests yet</div>}
                  {creditRequests.map(c => (
                    <div key={c.credit_id} className="border rounded-md p-2 text-xs" data-testid={`credit-req-${c.credit_id}`}>
                      <div className="flex justify-between"><b>{c.user_name}</b><Badge variant={c.status === 'pending' ? 'outline' : 'default'} className={c.status === 'approved' ? 'bg-[#1D4ED8]' : ''}>{c.status}</Badge></div>
                      <div className="text-[10px] text-[var(--bz-muted)]">{c.user_phone} · UTR: <span className="font-mono-bz">{c.utr}</span></div>
                      <div className="font-mono-bz font-bold mt-1">₹{c.amount}</div>
                      {c.screenshot_url && <ImageZoom src={c.screenshot_url} testId={`credit-shot-${c.credit_id}`}><img src={c.screenshot_url} alt="" className="h-20 mt-1 rounded-sm border cursor-zoom-in" /></ImageZoom>}
                      {c.status === 'pending' && (
                        <div className="flex gap-1 mt-2">
                          <Button size="sm" className="bg-[#1D4ED8] hover:bg-[#1E3A8A] h-7 text-[10px]" onClick={() => decideCredit(c.credit_id, true)} data-testid={`approve-credit-${c.credit_id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Approve</Button>
                          <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => decideCredit(c.credit_id, false)} data-testid={`reject-credit-${c.credit_id}`}><XCircle className="h-3 w-3 mr-1" />Reject</Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {isTL && twdRequests.filter(w => w.status === 'pending').length > 0 && (
              <Card className="p-4 border-yellow-400" data-testid="tl-withdraw-queue">
                <div className="font-bold display mb-2 text-sm flex items-center gap-2"><Wallet className="h-4 w-4 text-yellow-700" />Withdrawal requests ({twdRequests.filter(w => w.status === 'pending').length})</div>
                <div className="space-y-2 max-h-[260px] overflow-y-auto bz-scroll">
                  {twdRequests.filter(w => w.status === 'pending').map(w => (
                    <div key={w.withdrawal_id} className="border rounded-md p-2 text-xs">
                      <div className="flex justify-between"><b>{w.user_name}</b><span className="text-[10px] text-[var(--bz-muted)]">{w.user_phone}</span></div>
                      <div className="font-mono-bz font-bold mt-1">₹{w.amount}</div>
                      <div className="flex gap-1 mt-2">
                        <Button size="sm" className="bg-[#1D4ED8] hover:bg-[#1E3A8A] h-7 text-[10px]" onClick={async () => {
                          const fd = new FormData(); fd.append('withdrawal_id', w.withdrawal_id); fd.append('approve', 'true');
                          await api.post('/tambola/withdraw-decide', fd); toast.success('Marked as paid');
                          const tw = await api.get(`/tambola/rooms/${active.room_id}/withdraw-requests`); setTwdRequests(tw.data);
                        }} data-testid={`approve-twd-${w.withdrawal_id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Mark paid</Button>
                        <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={async () => {
                          const fd = new FormData(); fd.append('withdrawal_id', w.withdrawal_id); fd.append('approve', 'false');
                          await api.post('/tambola/withdraw-decide', fd); toast.success('Rejected');
                          const tw = await api.get(`/tambola/rooms/${active.room_id}/withdraw-requests`); setTwdRequests(tw.data);
                        }} data-testid={`reject-twd-${w.withdrawal_id}`}><XCircle className="h-3 w-3 mr-1" />Reject</Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            {isTL && (
              <Card className="p-4" data-testid="tl-profit-dashboard">
                <div className="font-bold display mb-2 text-sm flex items-center gap-2"><Wallet className="h-4 w-4 text-[#1E3A8A]" />Profit dashboard</div>
                <div className="grid grid-cols-3 gap-2 mb-3">
                  <div className="bz-card p-2"><div className="text-[9px] uppercase tracking-wider text-[var(--bz-muted)]">Tickets</div><div className="text-lg font-black display" data-testid="total-tickets">{playersData.totals.tickets}</div></div>
                  <div className="bz-card p-2"><div className="text-[9px] uppercase tracking-wider text-[var(--bz-muted)]">My fee</div><div className="text-lg font-black display text-[#1E3A8A]" data-testid="tl-fee-earned">₹{playersData.totals.tl_fee_earned}</div></div>
                  <div className="bz-card p-2"><div className="text-[9px] uppercase tracking-wider text-[var(--bz-muted)]">Prize pool</div><div className="text-lg font-black display">₹{playersData.totals.prize_pool}</div></div>
                </div>
                <div className="space-y-1 max-h-[200px] overflow-y-auto bz-scroll">
                  {playersData.players.length === 0 && <div className="text-xs text-[var(--bz-muted)]">No players have credits yet.</div>}
                  {playersData.players.map(p => (
                    <div key={p.user_id} className="flex items-center justify-between text-xs border-b py-1" data-testid={`player-${p.user_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold truncate">{p.user_name}</div>
                        <div className="text-[10px] text-[var(--bz-muted)]">{p.tickets_bought} tickets · paid ₹{p.amount_paid} · bal ₹{p.credits}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-mono-bz font-bold text-[#1E3A8A]">+₹{p.tl_fee_earned}</div>
                        <div className="text-[9px] text-[var(--bz-muted)]">my fee</div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="font-bold display">Your tickets ({tickets.length})</div>
                {active.status === 'live' && (
                  <Badge className="bg-[#1D4ED8] text-white animate-pulse">LIVE · {active.called_numbers?.length || 0}/90</Badge>
                )}
              </div>

              {/* Big LAST CALLED ball — shown above the tickets */}
              {active.status === 'live' && (
                <div className="mb-4 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#3B82F6] rounded-2xl p-4 flex items-center justify-center gap-4 shadow-inner">
                  <div className="text-center">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/70 font-bold">Last called</div>
                    {active.called_numbers?.length ? (
                      <div key={active.called_numbers[active.called_numbers.length - 1]} className="mt-1 h-24 w-24 rounded-full bg-white text-[#1E3A8A] text-5xl font-black display grid place-items-center shadow-2xl ring-4 ring-white/30 animate-in zoom-in-50 duration-500" data-testid="ticket-area-last-number">
                        {active.called_numbers[active.called_numbers.length - 1]}
                      </div>
                    ) : (
                      <div className="mt-1 h-24 w-24 rounded-full bg-white/20 text-white/70 text-xs font-bold grid place-items-center">Waiting…</div>
                    )}
                  </div>
                  {active.called_numbers?.length > 1 && (
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-[0.25em] text-white/70 font-bold mb-1">Previous</div>
                      <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto">
                        {active.called_numbers.slice(0, -1).reverse().slice(0, 12).map(n => (
                          <span key={n} className="bg-white/20 text-white text-xs font-bold rounded-full h-7 w-7 grid place-items-center">{n}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {active.status === 'lobby' && (
                <div className="flex gap-2 mb-3 flex-wrap">
                  <Button size="sm" onClick={() => buyTicket(1)} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="buy-ticket-1" disabled={balance < active.ticket_price}><Ticket className="h-3 w-3 mr-1" />Buy 1 (₹{active.ticket_price})</Button>
                  {active.is_multi_ticket && <Button size="sm" variant="outline" onClick={() => buyTicket(5)} data-testid="buy-ticket-5" disabled={balance < 5 * active.ticket_price}>+5 (₹{5 * active.ticket_price})</Button>}
                  {isTL && balance < active.ticket_price && (
                    <Button size="sm" variant="outline" onClick={() => { setTlSelfAmount(active.ticket_price); setTlSelfOpen(true); }} className="text-[#1E3A8A]" data-testid="tl-quick-deposit"><Wallet className="h-3 w-3 mr-1" />Add credits</Button>
                  )}
                </div>
              )}
              <div className="space-y-3 max-h-[480px] overflow-y-auto bz-scroll">
                {tickets.map(t => (
                  <div key={t.ticket_id} className="bg-[var(--bz-soft)] rounded-md p-2">
                    <TicketGrid ticket={t} called={active.called_numbers || []} />
                    <div className="flex flex-wrap gap-1 mt-2 items-center">
                      {active.status === 'live' && ['jaldi5', 'line1', 'line2', 'line3', 'full_house'].map(p => (
                        <Button key={p} size="sm" variant="outline" className="text-[10px] px-2 h-7" onClick={() => claim(t.ticket_id, p)} data-testid={`claim-${p}-${t.ticket_id}`}>
                          <Trophy className="h-3 w-3 mr-1" />{p.replace('_', '')}
                        </Button>
                      ))}
                      {active.status === 'lobby' && (
                        <Button size="sm" variant="outline" className="text-[10px] px-2 h-7 ml-auto text-red-600 border-red-200 hover:bg-red-50" onClick={() => undoTicket(t.ticket_id)} data-testid={`undo-ticket-${t.ticket_id}`}>
                          <RotateCcw className="h-3 w-3 mr-1" />Undo · refund ₹{active.ticket_price}
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                {tickets.length === 0 && active.status === 'lobby' && (
                  <div className="text-xs text-[var(--bz-muted)] py-3 text-center">
                    {isTL ? 'Click "Self deposit" to add credits, then buy tickets to play in your own game.' : 'Add credits, then buy tickets to start playing.'}
                  </div>
                )}
              </div>

              {/* TL Call/Start button BELOW tickets */}
              {isTL && (
                <div className="mt-4 pt-3 border-t border-[var(--bz-line)] flex gap-2">
                  {active.status === 'lobby' && (
                    <Button onClick={startRoom} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2 flex-1 h-12 text-base font-bold display" data-testid="start-game-btn-bottom">
                      <Play className="h-5 w-5" />Start the game
                    </Button>
                  )}
                  {active.status === 'live' && (
                    <Button onClick={callNumber} className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] hover:from-[#1E3A8A] hover:to-[#1D4ED8] gap-2 flex-1 h-14 text-lg font-bold display shadow-lg" data-testid="call-number-btn-bottom">
                      <Megaphone className="h-5 w-5" />Call next number
                    </Button>
                  )}
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* TL self-deposit dialog */}
        <Dialog open={tlSelfOpen} onOpenChange={setTlSelfOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Self deposit credits</DialogTitle>
            </DialogHeader>
            <div className="text-xs text-[var(--bz-muted)]">
              As team leader, add credits to your own balance instantly so you can buy tickets and play in this game. Tracked in audit log as a TL self-deposit (no approval needed).
            </div>
            <div className="space-y-2">
              <Label htmlFor="tl-self-amount">Amount (₹)</Label>
              <Input
                id="tl-self-amount"
                type="number"
                min={1}
                value={tlSelfAmount}
                onChange={e => setTlSelfAmount(e.target.value)}
                data-testid="tl-self-amount-input"
              />
              <div className="flex flex-wrap gap-1.5">
                {[active.ticket_price, active.ticket_price * 5, active.ticket_price * 10, 500, 1000].filter((v, i, a) => v > 0 && a.indexOf(v) === i).map(v => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setTlSelfAmount(v)}
                    className="text-[10px] px-2 py-1 rounded-full bg-[var(--bz-green-tint)] text-[#1E3A8A] hover:bg-[#DCFCE7]"
                    data-testid={`tl-self-preset-${v}`}
                  >
                    ₹{v}
                  </button>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTlSelfOpen(false)}>Cancel</Button>
              <Button
                onClick={submitTlSelfCredit}
                disabled={tlSelfBusy || !tlSelfAmount}
                className="bg-[#1D4ED8] hover:bg-[#1E3A8A]"
                data-testid="tl-self-deposit-submit"
              >
                {tlSelfBusy ? 'Adding…' : 'Add credits'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Credit request dialog */}
        <Dialog open={creditOpen} onOpenChange={setCreditOpen}>
          <DialogContent>
            <DialogHeader><DialogTitle>Request credit from team leader</DialogTitle></DialogHeader>
            {active.tl_payment && (
              <div className="bg-[var(--bz-green-tint)] rounded-md p-3 text-xs">
                <div className="font-bold display mb-1">Send payment to:</div>
                {active.tl_payment.upi && <div>UPI: <span className="font-mono-bz">{active.tl_payment.upi}</span></div>}
                {active.tl_payment.phone && <div>Phone: {active.tl_payment.phone}</div>}
                {active.tl_payment.bank && <div>Bank: {active.tl_payment.bank} A/C {active.tl_payment.account} IFSC {active.tl_payment.ifsc}</div>}
                {active.tl_payment.qr_image_url && <img src={active.tl_payment.qr_image_url} alt="QR" className="h-32 mt-2" />}
              </div>
            )}
            <div className="space-y-3">
              <div><Label>Amount paid ₹</Label><Input type="number" value={creditForm.amount} onChange={e => setCreditForm({ ...creditForm, amount: Number(e.target.value) })} data-testid="credit-amount" /></div>
              <div><Label>UTR / Transaction ID</Label><Input value={creditForm.utr} onChange={e => setCreditForm({ ...creditForm, utr: e.target.value })} data-testid="credit-utr" /></div>
              <div><Label>Payment screenshot</Label>
                <UploadField
                  value={creditForm.screenshot_url}
                  onChange={(url) => setCreditForm({ ...creditForm, screenshot_url: url })}
                  onStatusChange={setCreditUploading}
                  testId="credit-screenshot"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreditOpen(false)}>Cancel</Button>
              <Button onClick={submitCreditRequest} disabled={creditUploading || !creditForm.screenshot_url} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="submit-credit-request">Send request</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Share dialog (TL only) */}
        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Invite a player</DialogTitle></DialogHeader>
            <p className="text-sm text-[var(--bz-muted)]">Only people with this link can join. Customise the message and send via WhatsApp / SMS / any chat.</p>
            <div className="grid grid-cols-[1fr_120px] gap-2">
              <div><Label className="text-xs">Player name (optional)</Label><Input value={inviteTo.name} onChange={e => setInviteTo({ ...inviteTo, name: e.target.value })} placeholder="Ramesh" data-testid="invite-name" /></div>
              <div><Label className="text-xs">Tickets</Label><Input type="number" min={1} value={inviteTo.tickets} onChange={e => setInviteTo({ ...inviteTo, tickets: Math.max(1, Number(e.target.value)) })} data-testid="invite-tickets" /></div>
            </div>
            <Textarea
              rows={9}
              value={inviteMessage(active, inviteTo.name, inviteTo.tickets)}
              readOnly
              className="text-xs font-mono-bz"
              data-testid="invite-message-preview"
            />
            <div className="font-mono-bz text-[10px] bg-[var(--bz-soft)] p-2 rounded-sm break-all">{inviteLink(active)}</div>
            <DialogFooter className="flex-wrap gap-2">
              <Button variant="outline" onClick={() => setShareOpen(false)}>Close</Button>
              <Button onClick={() => copyLink(active)} variant="outline" className="gap-2" data-testid="copy-just-link"><Copy className="h-4 w-4" />Copy link</Button>
              <Button onClick={() => copyInviteMessage(active)} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2" data-testid="copy-invite-message"><Copy className="h-4 w-4" />Copy message</Button>
              <Button asChild className="gap-2 bg-[#25D366] hover:bg-[#1ebe5b] text-white" data-testid="share-whatsapp">
                <a target="_blank" rel="noreferrer" href={`https://wa.me/?text=${encodeURIComponent(inviteMessage(active, inviteTo.name, inviteTo.tickets))}`}>WhatsApp</a>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-screen-xl mx-auto">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tighter display">Fun Time</h1>
          <p className="text-sm text-[var(--bz-muted)] mt-1">Host or join private Tambola rooms — only people with your invite link can play.</p>
        </div>
        <Button onClick={() => setCreating(true)} className="bg-[#1D4ED8] hover:bg-[#1E3A8A] gap-2" data-testid="create-room-btn">
          <Plus className="h-4 w-4" />Host a game
        </Button>
      </div>

      <Card className="p-5 mb-6 border-[#1D4ED8]/40">
        <div className="font-bold display mb-2 flex items-center gap-2"><LinkIcon className="h-4 w-4 text-[#1E3A8A]" />Join with invite link</div>
        <div className="flex gap-2">
          <Input placeholder="Paste invite link or token here" value={joinTokenInput} onChange={e => setJoinTokenInput(e.target.value)} data-testid="join-token-input" />
          <Button onClick={joinViaLink} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="join-link-btn">Join</Button>
        </div>
        <p className="text-[11px] text-[var(--bz-muted)] mt-2">Rooms are private — ask your team leader for the link.</p>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="font-bold display mb-3">Rooms I&apos;m hosting ({myRooms.as_team_leader.length})</div>
          {myRooms.as_team_leader.length === 0 && <div className="text-xs text-[var(--bz-muted)]">You&apos;re not hosting any games. Click &quot;Host a game&quot; to start one.</div>}
          {myRooms.as_team_leader.map(r => (
            <div key={r.room_id} className="border rounded-md p-3 flex items-center justify-between mb-2" data-testid={`my-room-${r.room_id}`}>
              <div>
                <div className="font-bold">{r.name}</div>
                <div className="text-[11px] text-[var(--bz-muted)]">₹{r.ticket_price}/ticket · {r.status}</div>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => copyLink(r)} data-testid={`copy-link-${r.room_id}`}><Copy className="h-3 w-3" /></Button>
                <Button size="sm" className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" onClick={() => openRoom(r)} data-testid={`open-room-${r.room_id}`}>Open</Button>
              </div>
            </div>
          ))}
        </Card>
        <Card className="p-4">
          <div className="font-bold display mb-3">Games I&apos;ve joined ({myRooms.as_player.length})</div>
          {myRooms.as_player.length === 0 && <div className="text-xs text-[var(--bz-muted)]">You haven&apos;t joined any games yet. Use an invite link above.</div>}
          {myRooms.as_player.map(r => (
            <div key={r.room_id} className="border rounded-md p-3 flex items-center justify-between mb-2">
              <div>
                <div className="font-bold">{r.name}</div>
                <div className="text-[11px] text-[var(--bz-muted)]">TL: {r.team_leader_name} · ₹{r.ticket_price}</div>
              </div>
              <Button size="sm" className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" onClick={() => openRoom(r)}>Open</Button>
            </div>
          ))}
        </Card>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bz-scroll">
          <DialogHeader><DialogTitle>Host a Tambola game</DialogTitle></DialogHeader>
          <Tabs defaultValue="game">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="game">Game settings</TabsTrigger>
              <TabsTrigger value="payment">My payment details</TabsTrigger>
            </TabsList>
            <TabsContent value="game" className="space-y-3">
              <div><Label>Room name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="room-name" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Ticket ₹</Label><Input type="number" value={form.ticket_price} onChange={e => setForm({ ...form, ticket_price: Number(e.target.value) })} data-testid="ticket-price" /></div>
                <div><Label>Max players</Label><Input type="number" value={form.max_players} onChange={e => setForm({ ...form, max_players: Number(e.target.value) })} /></div>
                <div><Label>TL fee %</Label><Input type="number" value={form.tl_fee_pct} min={2} onChange={e => setForm({ ...form, tl_fee_pct: Number(e.target.value) })} /></div>
              </div>
              <div className="flex items-center gap-2"><Switch checked={form.is_multi_ticket} onCheckedChange={(v) => setForm({ ...form, is_multi_ticket: v })} data-testid="multi-ticket-switch" /><Label>Multi-ticket game (system auto-splits prize pool)</Label></div>
              {!form.is_multi_ticket && (
                <div>
                  <Label className="text-xs uppercase tracking-wider">Single-ticket prize amounts</Label>
                  <div className="grid grid-cols-5 gap-2 mt-1">
                    {['jaldi5', 'line1', 'line2', 'line3', 'full_house'].map(k => (
                      <div key={k}>
                        <div className="text-[10px] text-[var(--bz-muted)]">{k}</div>
                        <Input type="number" value={form.custom_prizes[k]} onChange={e => setForm({ ...form, custom_prizes: { ...form.custom_prizes, [k]: Number(e.target.value) } })} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </TabsContent>
            <TabsContent value="payment" className="space-y-3">
              <p className="text-xs text-[var(--bz-muted)]">Players see these payment details. They pay you, upload UTR + screenshot, and you approve the credit request before they can buy tickets.</p>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>UPI ID</Label><Input value={form.tl_payment.upi} onChange={e => setForm({ ...form, tl_payment: { ...form.tl_payment, upi: e.target.value } })} placeholder="yourname@upi" data-testid="tl-upi" /></div>
                <div><Label>Phone (PhonePe / GPay)</Label><Input value={form.tl_payment.phone} onChange={e => setForm({ ...form, tl_payment: { ...form.tl_payment, phone: e.target.value } })} data-testid="tl-phone" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Bank</Label><Input value={form.tl_payment.bank} onChange={e => setForm({ ...form, tl_payment: { ...form.tl_payment, bank: e.target.value } })} /></div>
                <div><Label>Account no.</Label><Input value={form.tl_payment.account} onChange={e => setForm({ ...form, tl_payment: { ...form.tl_payment, account: e.target.value } })} /></div>
                <div><Label>IFSC</Label><Input value={form.tl_payment.ifsc} onChange={e => setForm({ ...form, tl_payment: { ...form.tl_payment, ifsc: e.target.value } })} /></div>
              </div>
              <div>
                <Label>QR code image (optional)</Label>
                <input type="file" accept="image/*" onChange={async (e) => {
                  const file = e.target.files?.[0]; if (!file) return;
                  const res = await uploadImage(file);
                  if (res) setForm(f => ({ ...f, tl_payment: { ...f.tl_payment, qr_image_url: res.url } }));
                  e.target.value = '';
                }} data-testid="tl-qr-upload" />
                {form.tl_payment.qr_image_url && <img src={form.tl_payment.qr_image_url} alt="QR" className="h-24 mt-1 rounded-sm" />}
              </div>
              <div><Label>Instructions to players</Label><Textarea rows={2} value={form.tl_payment.instructions} onChange={e => setForm({ ...form, tl_payment: { ...form.tl_payment, instructions: e.target.value } })} /></div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={createRoom} className="bg-[#1D4ED8] hover:bg-[#1E3A8A]" data-testid="submit-room-button">Create room</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FunTime;
